import React, { useEffect, useState } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { EntitlementsProvider } from './context/EntitlementsContext';
import { UpgradeProvider } from './context/UpgradeContext';
import { MaintenanceProvider } from './context/MaintenanceContext';
import MaintenanceBanner, { MaintenanceTakeover, AdminTakeoverWarning } from './components/MaintenanceBanner';
import Landing from './pages/Landing';
import AuthCallback from './pages/AuthCallback';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Floorplan from './pages/Floorplan';
import Tools from './pages/Tools';
import Report from './pages/Report';
import Shared from './pages/Shared';
import InviteClaim from './pages/InviteClaim';
import Admin from './pages/Admin';
import BuilderLogin from './pages/BuilderLogin';
import BuilderConsume from './pages/BuilderConsume';
import BuilderDashboard from './pages/BuilderDashboard';
import Account from './pages/Account';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import DeveloperUpgrade from './pages/DeveloperUpgrade';
import Team from './pages/Team';
import InviteAcceptTeam from './pages/InviteAcceptTeam';
import BuilderSnagging from './pages/BuilderSnagging';
import OnboardingTour from './components/OnboardingTour';
import { Toaster, toast } from 'sonner';

/** Wipe legacy localStorage test data once, on first app load after Phase 1.
 *  Snag data now lives in MongoDB / Object Storage — any pre-migration
 *  `defectr:data:*` entries would just confuse the new hooks. */
function useLegacyStorageWipe() {
    useEffect(() => {
        try {
            const legacy = Object.keys(localStorage).filter(k => k.startsWith('defectr:data:'));
            if (legacy.length === 0) return;
            legacy.forEach(k => localStorage.removeItem(k));
            localStorage.setItem('defectr:migrated:v2', '1');
            toast.info('DefectR has moved to cloud storage. Any local test data has been cleared — start fresh.', { duration: 6000 });
        } catch { /* noop */ }
    }, []);
}

function Protected({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return (
        <div className="min-h-screen flex items-center justify-center">
            <img
                src="/defectr-logo.png"
                alt="DefectR"
                className="h-12 w-auto animate-pulse"
            />
        </div>
    );
    if (!user) return <Navigate to="/" replace state={{ from: location }} />;
    return children;
}

import CheckoutReturnHandler from './components/CheckoutReturnHandler';

/** Mount the onboarding tour at the route level so it shows on the first
 *  authenticated page load. Listens for a 'defectr:tour:restart' event so
 *  the Help drawer can re-trigger it on demand. */
function TourHost() {
    const [forceOpen, setForceOpen] = useState(false);
    useEffect(() => {
        const onRestart = () => { setForceOpen(true); };
        window.addEventListener('defectr:tour:restart', onRestart);
        return () => window.removeEventListener('defectr:tour:restart', onRestart);
    }, []);
    return (
        <OnboardingTour
            forceOpen={forceOpen}
            onClose={() => setForceOpen(false)}
        />
    );
}

function AppRouter() {
    const location = useLocation();
    useLegacyStorageWipe();
    // Detect session_id on ANY route - process auth callback synchronously
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }
    // Show the tour on the dashboard root (/app) but skip it when the
    // user has deep-linked to a property page (e.g. an invite email
    // brought them straight to /app/project/<id>) — the tour would
    // otherwise overlay the page they actually came to use.
    const showTour = (location.pathname === '/app' || location.pathname === '/app/')
        || location.pathname.startsWith('/admin');
    return (
        <>
            <CheckoutReturnHandler />
            <MaintenanceBanner />
            <AdminTakeoverWarning />
            <MaintenanceTakeover>
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/developer" element={<DeveloperUpgrade />} />
                    <Route path="/team" element={<Protected><Team /></Protected>} />
                    <Route path="/team/accept" element={<InviteAcceptTeam />} />
                    <Route path="/snag/:slug" element={<BuilderSnagging />} />
                    <Route path="/shared/:shortId" element={<Shared />} />
                    <Route path="/invite/:code" element={<InviteClaim />} />
                    <Route path="/builder/login" element={<BuilderLogin />} />
                    <Route path="/builder/auth/consume" element={<BuilderConsume />} />
                    <Route path="/builder/dashboard" element={<BuilderDashboard />} />
                    <Route path="/app" element={<Protected><Projects /></Protected>} />
                    <Route path="/app/project/:id" element={<Protected><ProjectDetail /></Protected>} />
                    <Route path="/app/floorplan" element={<Protected><Floorplan /></Protected>} />
                    <Route path="/app/tools" element={<Protected><Tools /></Protected>} />
                    <Route path="/app/report" element={<Protected><Report /></Protected>} />
                    <Route path="/app/account" element={<Protected><Account /></Protected>} />
                    <Route path="/admin" element={<Protected><Admin /></Protected>} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </MaintenanceTakeover>
            {showTour && <TourHost />}
        </>
    );
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <EntitlementsProvider>
                        <MaintenanceProvider>
                            <UpgradeProvider>
                                <AppRouter />
                                <Toaster richColors position="top-center" />
                            </UpgradeProvider>
                        </MaintenanceProvider>
                    </EntitlementsProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}
